import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { useSmoothScroll } from './hooks/useSmoothScroll';
import { InteractionLayer } from './components/InteractionLayer';
import { PageLoader } from './components/PageLoader';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SeoManager } from './components/SeoManager';
import { CmsProvider, cmsFetch } from './lib/cms';

const Home=lazy(()=>import('./pages/Home').then(m=>({default:m.Home})));
const Generic=()=>import('./pages/GenericPages');
const ListingPage=lazy(()=>Generic().then(m=>({default:m.ListingPage})));
const DetailPage=lazy(()=>Generic().then(m=>({default:m.DetailPage})));
const SystemBuilderPage=lazy(()=>Generic().then(m=>({default:m.SystemBuilderPage})));
const BookCallPage=lazy(()=>Generic().then(m=>({default:m.BookCallPage})));
const ManageBookingPage=lazy(()=>Generic().then(m=>({default:m.ManageBookingPage})));
const LegalPage=lazy(()=>Generic().then(m=>({default:m.LegalPage})));
const ErrorPage=lazy(()=>Generic().then(m=>({default:m.ErrorPage})));

function CmsAnalytics(){
  const {pathname}=useLocation();
  useEffect(()=>{cmsFetch('/api/analytics/pageview',{method:'POST',body:JSON.stringify({path:pathname,referrer:document.referrer,screen:`${innerWidth}x${innerHeight}`})}).catch(()=>{})},[pathname]);
  return null;
}

function Shell(){
  useSmoothScroll();
  return <CmsProvider><>
    <PageLoader/>
    <SeoManager/>
    <CmsAnalytics/>
    <InteractionLayer/>
    <Header/>
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home/>}/>
          <Route path="/services" element={<ListingPage type="services"/>}/>
          <Route path="/services/:slug" element={<DetailPage type="service"/>}/>
          <Route path="/work" element={<ListingPage type="work"/>}/>
          <Route path="/work/:slug" element={<DetailPage type="case study"/>}/>
          <Route path="/insights" element={<ListingPage type="insights"/>}/>
          <Route path="/insights/:slug" element={<DetailPage type="insight"/>}/>
          <Route path="/system-builder" element={<SystemBuilderPage/>}/>
          <Route path="/book-call" element={<BookCallPage/>}/>
          <Route path="/manage-booking" element={<ManageBookingPage/>}/>
          <Route path="/careers" element={<ListingPage type="careers"/>}/>
          <Route path="/careers/:slug" element={<DetailPage type="career"/>}/>
          <Route path="/privacy" element={<LegalPage type="privacy"/>}/>
          <Route path="/terms" element={<LegalPage type="terms"/>}/>
          <Route path="/cookies" element={<LegalPage type="cookies"/>}/>
          <Route path="/accessibility" element={<LegalPage type="accessibility"/>}/>
          <Route path="/error/400" element={<ErrorPage code="400" title="The request could not be understood."/>}/>
          <Route path="/error/401" element={<ErrorPage code="401" title="Authentication required."/>}/>
          <Route path="/error/403" element={<ErrorPage code="403" title="Access restricted."/>}/>
          <Route path="/error/404" element={<ErrorPage code="404" title="Nothing lives here."/>}/>
          <Route path="/error/408" element={<ErrorPage code="408" title="The request timed out."/>}/>
          <Route path="/error/429" element={<ErrorPage code="429" title="Too many requests."/>}/>
          <Route path="/error/500" element={<ErrorPage code="500" title="Something went wrong."/>}/>
          <Route path="/error/502" element={<ErrorPage code="502" title="The upstream service did not respond."/>}/>
          <Route path="/error/503" element={<ErrorPage code="503" title="Service temporarily unavailable."/>}/>
          <Route path="*" element={<ErrorPage code="404" title="Nothing lives here."/>}/>
        </Routes>
      </Suspense>
    </ErrorBoundary>
    <Footer/>
  </></CmsProvider>;
}

export default function App(){return <BrowserRouter><Shell/></BrowserRouter>}
